import { pathOr, prop } from 'ramda'
import React from 'react'

import { Checkbox } from '../../shapes/Marker'
import { Col, Grid } from '../../structure/Grid'
import { Fixed } from '../../../../../components/helpers/Position'
import { isFunction } from '../../../utils/helpers/types'
import { useForm, useFormState } from './FormHandler'
import Button from '../../actions/Button'
import Center from '../../helpers/Center'
import FormItem from './FormItem'
import Icon from '../../shapes/Icon'
import Panel from '../../structure/Panel'
import Separator from '../../helpers/Separator'
import Text, { Label } from '../../presentation/Text'

const DEFAULT_COL_WIDTH = 12.5

const ActionsCol = React.memo(({ path, index, removeIn, duplicateIn, hideDuplicateButton, onEdit }) => {
  return (
    <Col minWidth={4}>
      <Center gutter={0.4} height={3}>
        {!hideDuplicateButton && (
          <Col>
            <Icon name="BsLayers" color="dark3" onClick={() => duplicateIn([...path, index])} />
          </Col>
        )}
        {onEdit && (
          <Col>
            <Icon name="BsPencil" color="dark3" onClick={() => onEdit(index)} />
          </Col>
        )}
        <Col>
          <Icon name="BsTrash" color="red" onClick={() => removeIn([...path, index])} />
        </Col>
      </Center>
    </Col>
  )
})

const InputCol = React.memo(({ rootPath, path, field, index, width, disabled, ...props }) => {
  const isDisabledFunction = typeof disabled === 'function'
  return (
    <Col minWidth={width || DEFAULT_COL_WIDTH}>
      <FormItem
        path={[...rootPath, index, ...(path || []), field]}
        index={index}
        disabled={isDisabledFunction ? disabled(props.value, index) : disabled}
        {...props}
      />
    </Col>
  )
})

const MultiEditWarning = ({ path, showTotal }) => {
  const values = pathOr([], path, useForm()) || []
  const isAnySelected = Array.isArray(values) && values.some(prop('__multiEdit'))
  if (!isAnySelected) return false

  return (
    <Fixed bottom={0} left={0} right={0}>
      <Center>
        <Panel bgColor="orange" noRadiusBottom padding={1}>
          <Text color="white" bold>
            Selecionados: {values.filter(v => v.__multiEdit).length} - Todos os itens selecionados serão alterados
            simultaneamente
          </Text>
        </Panel>
      </Center>
    </Fixed>
  )
}

const BulkForm = ({
  path,
  fields,
  initialItemState,
  isEditing,
  disableMultiEdit,
  showTotal,
  hideDuplicateButton,
  hasInterpolations = false
}) => {
  path = path || []
  const values = pathOr([], path, useForm()) || []
  const { removeIn, duplicateIn, addIn, toggleMultiEdit, toggleAllMultiEdit } = useFormState()
  const isAllSelected = Array.isArray(values) && values?.every(prop('__multiEdit'))

  return (
    <div className="bulkForm">
      <Grid gutter={0.2}>
        {!disableMultiEdit && (
          <Col minWidth={1.7}>
            <Checkbox value={isAllSelected} onChange={() => toggleAllMultiEdit(path || [])} />
          </Col>
        )}
        {fields.map(field => (
          <Col key={field.path?.join('-') + field.name} minWidth={field.width || DEFAULT_COL_WIDTH}>
            <Label marginBottom={hasInterpolations ? 0 : 0.4}>{field.label}</Label>
          </Col>
        ))}
      </Grid>

      {Array.isArray(values) &&
        values?.map((value, index) => (
          <div key={value.reference}>
            <Grid gutter={0.2} key={value.reference}>
              {!disableMultiEdit && (
                <Col minWidth={1.7}>
                  <Separator weight={2} />
                  <Checkbox value={value.__multiEdit} onChange={() => toggleMultiEdit([...path, index])} />
                </Col>
              )}
              {fields.map(({ name, width, label, disabled, dynamicProps, ...props }) => (
                <InputCol
                  key={name}
                  width={width}
                  rootPath={path}
                  field={name}
                  index={index}
                  disabled={disabled ? (isFunction(disabled) ? disabled(value) : disabled) : false}
                  {...props}
                  {...(isFunction(dynamicProps) ? dynamicProps(value) : {})}
                />
              ))}

              {!isEditing && (
                <ActionsCol
                  path={path}
                  index={index}
                  removeIn={removeIn}
                  duplicateIn={duplicateIn}
                  hideDuplicateButton={hideDuplicateButton}
                />
              )}
            </Grid>
            <Separator size={hasInterpolations ? 2 : 0.4} />
          </div>
        ))}

      <Separator weight={4} />

      {!isEditing && (
        <Button
          transparent
          size="small"
          onClick={() => {
            if (isFunction(initialItemState)) {
              addIn(path || [], initialItemState())
            } else {
              addIn(path || [], initialItemState || {})
            }
          }}
        >
          Adicionar novo
        </Button>
      )}

      <Separator weight={120} />
      <MultiEditWarning path={path} showTotal={showTotal} />
    </div>
  )
}

export default BulkForm
